import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  AlertCircle,
  Loader2
} from 'lucide-react';
import { courseApi } from '../../services/courseApi';

const CourseQuiz = () => {
  const { courseId } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const loadQuiz = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get quiz data
        const response = await courseApi.getQuiz(courseId);
        console.log('Raw Quiz Response:', response); // Debug log

        if (!response.success) {
          throw new Error(response.error || 'Failed to load quiz');
        }

        // The quiz data is in response.data.linuxQuizQuestions
        const quizQuestions = response.data?.linuxQuizQuestions;
        console.log('Quiz Questions:', quizQuestions); // Debug log

        if (!quizQuestions || !Array.isArray(quizQuestions) || quizQuestions.length === 0) {
          throw new Error('No quiz questions available');
        }

        setQuiz(quizQuestions);
      } catch (error) {
        console.error('Quiz loading error:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
          <p className="text-gray-400 animate-pulse">Loading quiz questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="text-red-400 text-lg font-medium">{error}</p>
          <Link 
            to={`/learning/courses/${courseId}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-blue-400 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Course
          </Link>
        </div>
      </div>
    );
  }

  if (!quiz || quiz.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link 
            to={`/learning/courses/${courseId}`}
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Course
          </Link>

          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-4">Linux Commands Quiz</h1>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mt-8">
              <p className="text-red-400">No quiz questions available</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    try {
      if (Object.keys(answers).length === 0) {
        setError('Please answer at least one question');
        return;
      }

      setLoading(true);
      const formattedAnswers = Object.entries(answers).map(([id, answer]) => ({
        questionId: parseInt(id),
        answer
      }));

      const response = await courseApi.submitQuiz(courseId, formattedAnswers);

      if (response.success) {
        // Handle successful submission
        console.log('Quiz submitted successfully:', response.data);
      } else {
        throw new Error(response.error || 'Failed to submit quiz');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Navigation */}
        <Link 
          to={`/learning/courses/${courseId}`}
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Course
        </Link>

        {/* Quiz Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Linux Commands Quiz</h1>
          <p className="text-gray-400 mt-2">Test your knowledge of Linux commands and concepts</p>
          <div className="mt-4 text-sm text-gray-400">
            {Object.keys(answers).length} of {quiz.length} questions answered
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {quiz.map((question, index) => (
            <div 
              key={question.id}
              className="bg-gray-800 rounded-xl border border-gray-700"
            >
              <div className="p-6">
                <h3 className="text-lg font-medium text-white mb-4">
                  Question {index + 1} of {quiz.length}
                </h3>
                <p className="text-gray-200 mb-4">{question.question}</p>
                <div className="space-y-3">
                  {question.options.map((option, optionIndex) => (
                    <label 
                      key={optionIndex}
                      className={`
                        flex items-center gap-3 p-4 rounded-lg cursor-pointer
                        ${answers[question.id] === option 
                          ? 'bg-blue-500/10 border-blue-500/50' 
                          : 'bg-gray-700/50 border-gray-600/50 hover:bg-gray-600/50'
                        }
                        border transition-colors duration-200
                      `}
                    >
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={(e) => setAnswers(prev => ({
                          ...prev,
                          [question.id]: e.target.value
                        }))}
                        className="w-4 h-4 text-blue-500 bg-gray-700 border-gray-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-300">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="mt-8">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-center">{error}</p>
            </div>
          )}
          
          <button
            onClick={handleSubmit}
            disabled={loading || Object.keys(answers).length === 0}
            className={`
              w-full py-4 rounded-lg font-medium
              ${loading ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 
                Object.keys(answers).length > 0 ? 'bg-blue-500 hover:bg-blue-600 text-white' : 
                'bg-gray-700 text-gray-400 cursor-not-allowed'}
            `}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </div>
            ) : (
              `Submit Quiz (${Object.keys(answers).length}/${quiz.length} answered)`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseQuiz;